import { Heading, HStack, SimpleGrid, Stack, Text } from '@chakra-ui/react'
import { useEffect, useState } from 'react'
import { FiBookOpen, FiInfo, FiUsers } from 'react-icons/fi'
import { SelfStudyProps } from '../utils/types'
import { StudyHoursCard } from './StudyHoursCard'
import { StudyHoursTotal } from './StudyHoursTotal'

const getHours = (report: SelfStudyProps['report'], key: string) =>
  report?.educationalHours?.[key] ?? '0'

const asNumber = (value: string) => {
  const parsedValue = Number(value)
  return Number.isFinite(parsedValue) ? parsedValue : 0
}

export const StudyHoursSection = ({ report, updateReport }: SelfStudyProps) => {
  const [selfStudy, setSelfStudy] = useState(() =>
    getHours(report, 'self-study'),
  )
  const [supportTeamStudy, setSupportTeamStudy] = useState(() =>
    getHours(report, 'support-team-study'),
  )

  useEffect(() => {
    setSelfStudy(getHours(report, 'self-study'))
    setSupportTeamStudy(getHours(report, 'support-team-study'))
  }, [report])

  const updateSelfStudy = (value: string) => {
    setSelfStudy(value)
    updateReport({ educationalHours: { 'self-study': value } })
  }
  const updateSupportTeamStudy = (value: string) => {
    setSupportTeamStudy(value)
    updateReport({ educationalHours: { 'support-team-study': value } })
  }
  const total = asNumber(selfStudy) + asNumber(supportTeamStudy)

  return (
    <Stack
      border="1px solid"
      borderColor="blue.100"
      borderRadius="2xl"
      p={{ base: 4, md: 8 }}
      spacing={8}
    >
      <Stack spacing={2}>
        <Heading fontSize="xl">Study hours this month</Heading>
        <Text color="gray.600">
          Record time spent on health-related study outside scheduled Healing
          ALS sessions.
        </Text>
      </Stack>
      <HStack
        align="start"
        bg="blue.50"
        border="1px solid"
        borderColor="blue.100"
        borderRadius="xl"
        p={5}
        spacing={4}
      >
        <FiInfo aria-hidden color="#2b6cb0" size={28} />
        <Text>
          Do not include scheduled Healing ALS sessions recorded above.
        </Text>
      </HStack>
      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
        <StudyHoursCard
          accentColor="purple"
          description="Time you personally spent reading, watching videos, researching or completing other health-related learning."
          icon={FiBookOpen}
          label="Your self-study"
          note="Examples: books, articles, videos, courses"
          onChange={updateSelfStudy}
          value={selfStudy}
        />
        <StudyHoursCard
          accentColor="blue"
          description="Total time spent by family, friends, CALS or other support people on your behalf."
          icon={FiUsers}
          label="Support team study"
          note="Includes study completed by your support team"
          onChange={updateSupportTeamStudy}
          value={supportTeamStudy}
        />
      </SimpleGrid>
      <StudyHoursTotal total={total} />
    </Stack>
  )
}
